
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AccountDashboardWidgetBase from './AccountDashboardWidgetBase';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import { formatDuration, lastCallsPayloadInitial, useStyles } from './AccountDashboardCallActivityWidget.utils';
import { Routes } from '../../../routes/routes';
import { ReduxState } from '../../../store/types';
import classNames from 'classnames';
import { ReactComponent as CallActivity } from '../../../assets/accountDashboard/CallActivity.svg';
import { ReactComponent as IncomingCall } from '../../../assets/accountDashboard/IncomingCall.svg';
import { ReactComponent as OutgoingCall } from '../../../assets/accountDashboard/OutgoingCall.svg';
import { ReactComponent as FailedIncoming } from '../../../assets/accountDashboard/FailedIncoming.svg';
import { ReactComponent as FailedOutgoing } from '../../../assets/accountDashboard/FailedOutgoing.svg';
import { SipCall, SipCallType } from '../../../store/actions/ringgroups/payloads';
import { actions } from '../../../store';
import WebSocketDataProvider from '../../../components/WebSocketDataProvider/WebSocketDataProvider';
import Transfer from '../../../components/CallControls/Transfer';
import PutOnHold from '../../../components/CallControls/PutOnHold';
import Resume from '../../../components/CallControls/Resume';
import AddAnotherExtensionToCall from '../../../components/CallControls/AddAnotherExtensionToCall';
import Disconnect from '../../../components/CallControls/Disconnect';
import { PlayingFile } from '../../../store/reducers/calls/reducer';
import { cdrPlaybackKey } from '../../../utils/playbackIdentity';
import AudioPlayer from '../../../components/AudioPlayer/AudioPlayer';
import { CallHistory } from '../../../store/types/CallHistory';
import { convertUtcToUserLocalTime } from '../../../utils/dateWithTimezoneConversion';
import dayjs from '../../../services/customDayJs';
import PlayFileButton from '../../../components/PlayFileButton/PlayFileButton';
import CustomizedIconButton from '../../../components/IconButton/IconButton';
import {Assignment} from '@material-ui/icons';
import EmptyGridPlaceholder from '../../../components/DataGrid/EmptyGridPlaceholder';
import AccountDashboardLoader from './AccountDashboardLoader';
import CallRecordInformationTabsDialog from '../../../components/Calls/CallRecordInformationTabsDialog/CallRecordInformationTabsDialog';
import toast from 'react-hot-toast';
import { prepareCallRecordName } from '../../../utils/calls/prepareCallRecordName';
import AlertDialog from '../../../components/AlertDialog/AlertDialog';
import { DialogButton } from '../../../components/AlertDialog/DialogContainer';
import Config from '../../../config.json';
import { GetAccountDashboardCallsPayload } from '../../../store/actions/accountDashboard/payloads';
import { ExtensionType } from '../../../store/types/Extension';
import { AccountPermission } from '../../../store/types/Permission';

const AccountDashboardCallActivityWidget: React.VFC = () => {
    const classes = useStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const {isLoadingCalls, totalCalls, xdr_list, sipCallsList, lastCallsPayload, peerContacts} = useSelector(
        (state: ReduxState) => state.accountDashboard,
    );
    
    const timezoneOffset = useSelector<ReduxState, number>(
        (state) => state.generic.sessionData?.tz_offset || 0,
    );
    const dateFormat = useSelector<ReduxState, string>(
        (state) => state.generic.sessionData?.out_date_time_format ?? 'YYYY-MM-DD HH:mm:ss',
    );
    const currency = useSelector((state: ReduxState) => state.accountControlPanel?.accountDetails?.iso_4217 ?? 'USD');

    const [callRecordInformation, setCallRecordInformation] = useState<CallHistory | undefined>(undefined);

    const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [isDownloadingGoingOn, setIsDownloadingGoingOn] = useState<boolean[]>([]);

    const fetchData = useCallback((payload: GetAccountDashboardCallsPayload) => {
        dispatch(actions.getAccountDashboardCalls.request(payload));
    }, []);
    
    useEffect(() => {
        dispatch(actions.getSessionData.request());
        dispatch(actions.getAccountDashboardPeersContactList.request());
    }, []);

    const loaderRef = useRef<HTMLDivElement | null>(null);
    const fetchNextPage = useCallback(() => {
        
        if(isLoadingCalls) return;
        if(totalCalls === undefined) return;

        const maxReached = (totalCalls !== undefined) && ((xdr_list?.length ?? 0) >= (totalCalls ?? 0));
        if(maxReached) {
            return;
        }

        const nextPayload = {
            ...(lastCallsPayload ?? lastCallsPayloadInitial),
            offset: (lastCallsPayload?.offset ?? 0) + (lastCallsPayload?.limit ?? lastCallsPayloadInitial.limit)
        };

        fetchData(nextPayload);
    }, [isLoadingCalls, fetchData, lastCallsPayload, totalCalls, xdr_list, lastCallsPayloadInitial]);

    useEffect(() => {
        fetchData(lastCallsPayloadInitial);
    }, [fetchData, lastCallsPayloadInitial]);

    useEffect(() => {
        callRecordInformation && setIsDetailsModalOpen(true)
    }, [callRecordInformation]);

    const getCallIcon = useCallback((bit_flag: number, failed: number) => {
        const bf = bit_flag & 12;
        if(bf == 0 || bf == 4) {
            if(failed > 0) 
                return (<FailedOutgoing />);
            return (<OutgoingCall />);
        } else if(bf == 8 && failed > 0) {
            return (<FailedIncoming />);
        } else if(bf == 12) {
            //forwarded - show as incoming
            return (<IncomingCall />);
        }
        return (<IncomingCall />);
    }, []);
    
    const getCallIconForSip = useCallback((sipCall: SipCall) => {
        if(sipCall.type == SipCallType.Outgoing)
        {
            return getCallIcon(4, 0);
        }
            return getCallIcon(8, 0);
    }, []);

    const callStatusUpdateCallback = useCallback((call: SipCall) => {
        if(!call) {
            return;
        }
        dispatch(actions.updateSipCallStatusForAccountDashboardByWebSocketEvent.request(call));
    }, []);
    
    const recalcCallsDurationEvent = useCallback(() => {
        dispatch(actions.recalcSipCallsDurationForAcoountDashboard.request());
    }, []);

    const getCallerDestination = useCallback((xdr: CallHistory) => {

        const doSplit = (caller: string | undefined) => {
            const match = caller?.match(/^(.{1,})\s\((.{1,})\)/);
            let name: string | undefined;
            let extName: string | undefined = undefined;

            if(match) {
                name = match[2];
                extName = match[1];
            } else{
                name = caller;
            }

            return [name, extName];
        }

        const bf = (xdr?.bit_flags ?? 0) & 12;
        if(bf == 0 || bf == 4) {
            //outgoing
            return doSplit(xdr.CLD);
        }
        else if(bf == 12) {
            //forwarded - as incoming
            return doSplit(xdr.CLI);
        }
        else {
            //incoming
            return doSplit(xdr.CLI);
        }
    }, []);
    
    const getCallerDestinationForSipCall = useCallback((sipCall: SipCall) => {

        let extCode: string | undefined = ''; 
        let extName: string | undefined = ''; 

        if(sipCall.type == 'incoming') {
            extCode = sipCall?.caller?.extension_id;
            extName = sipCall.caller?.display_id;
        }
        else {
            extCode = sipCall?.callee?.extension_id;
            extName = sipCall.callee?.display_id;
        }
        if(extCode?.length && peerContacts?.length) {
            const foundExtPeer = peerContacts.find(e => e.extension_id === extCode?.trim());
            extName = foundExtPeer?.extension_name ?? extName;
        }
        return [extName ?? '', extCode];
    }, [peerContacts]);

    const formatDate = useCallback((time: string | undefined) => {
        if(!time) return '';

        const date = convertUtcToUserLocalTime(
            time,
            timezoneOffset || 0,
            dateFormat,
        ).split(' ');
        
        const todayDate = convertUtcToUserLocalTime(
            dayjs.utc().format(dateFormat),
            timezoneOffset || 0,
            dateFormat,
        ).split(' ')[0];
        
        const datePart = date[0] === todayDate
            ? undefined
            : date[0];
        const timePart = date[1] + (date.length >= 3 ? ' ' + date[2] : '');
        
        return datePart 
            ? [datePart, timePart]
            : ['', timePart];
    }, [timezoneOffset, dateFormat]);

    const toggleTooltipVisibility = useCallback((index: number) => {
        const newDownloading = [...isDownloadingGoingOn];
        newDownloading[index] = false;
        setIsDownloadingGoingOn(newDownloading);
    }, [isDownloadingGoingOn, setIsDownloadingGoingOn]);

    const downloadFile = useCallback((
        i_xdr: number,
        call_recording_id: string,
        fileName: string,
        index: number,
    ) => {
        const newDownloading = [...isDownloadingGoingOn];
        newDownloading[index] = true;
        setIsDownloadingGoingOn(newDownloading);
        toast(t<string>('tooltips:ringGroups.downloadWillStartShortly'));
        dispatch(
            actions.getExtensionCallHistoryFile.request({
                i_xdr,
                call_recording_id,
                fileName,
                callback: () => toggleTooltipVisibility(index),
            }),
        );
    }, [isDownloadingGoingOn, setIsDownloadingGoingOn, toggleTooltipVisibility]);

    const removeItems = useCallback((items: CallHistory[]) => {
        dispatch(actions.removeCallRecordings.request({
                items,
                callback: () => {
                    dispatch(actions.clearCallRecordingIds.request(items));
                }
            }),
        );
        setCallRecordInformation(undefined);
    }, [setCallRecordInformation, totalCalls, xdr_list]);

    const deleteCallRecording = useCallback(() => {
        callRecordInformation && removeItems([callRecordInformation]);
        setIsRemoveModalOpen(false);
    }, [callRecordInformation, removeItems, setIsRemoveModalOpen]);

    useEffect(() => {
        const observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                fetchNextPage();
            }
        },
        {
            root: null,       // viewport
            rootMargin: '100px',
            threshold: 0,
        }
        );

        const el = loaderRef.current;
        if (el) observer.observe(el);

        return () => {
        if (el) observer.unobserve(el);
        };
    }, [loaderRef.current, isLoadingCalls, fetchNextPage]);
    
    const filesList = useMemo(() => {
        return [(callRecordInformation?.cr_download_ids?.map(e => { return {
                i_xdr: callRecordInformation.i_xdr,
                h323_conf_id: callRecordInformation.h323_conf_id,
                call_recording_id: e,
                playbackKey: cdrPlaybackKey(e, callRecordInformation.i_xdr)
            } as PlayingFile; }) ?? [])];
    }, [callRecordInformation]);

    const accounts = useMemo(() => {
        return peerContacts?.map(e => {
            return {
                extension_id: e.extension_id,
                extension_name: e.extension_name,
                i_account: e.i_account
            } as ExtensionType;
        }) ?? [];
    }, [peerContacts]);

    return (<AccountDashboardWidgetBase
        route={Routes.AccountCallActivity}
        icon={<CallActivity />}
        label={t('common:callActivity')}
        customWidgetContentClass={classes.customWidgetContentClass}
        permission={AccountPermission.Dashboard.CallActivity.value}
    >
        {sipCallsList?.map((sipCall, indx) => <div key={'sip_call_' + indx} className={classNames(classes.callEntry, classes.activeCall)}>
            <div className={classes.callEntryLeftItem}>
                <div className={classes.callEntryIconContainer}>
                    {getCallIconForSip(sipCall)}
                </div>
                <div className={classes.callDetailsContainer}>
                    <div className={classes.caller}>{getCallerDestinationForSipCall(sipCall)[0]}</div>
                    <div className={classes.secondLineContainer}>
                        <div className={classes.callerExt}>
                            {getCallerDestinationForSipCall(sipCall)[1]
                                ? t('screens:accountDashboard.ext') + ' ' + getCallerDestinationForSipCall(sipCall)[1]
                                : ''
                            }
                        </div>
                        {getCallerDestinationForSipCall(sipCall)[1] && sipCall.duration && (
                            <div className={classes.secondLineSeparator}>
                                • 
                            </div>
                        )}
                        <div className={classes.durationText}>
                            {sipCall.durationFormatSec}
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.callEntryRightItem}>
                <Transfer activeCallEntity={sipCall}
                    i_customer={sipCall.i_customer ?? ''}
                    accounts={accounts}
                    sipCalls={sipCallsList}
                />
                <div className={classes.actionButtonSpacer}></div>
                <PutOnHold activeCallEntity={sipCall} />
                <Resume activeCallEntity={sipCall} />
                <div className={classes.actionButtonSpacer}></div>
                <AddAnotherExtensionToCall activeCallEntity={sipCall}
                    i_customer={sipCall.i_customer ?? ''}
                    accounts={accounts}
                    sipCalls={sipCallsList}
                />
                <div className={classes.actionButtonSpacer}></div>
                <Disconnect activeCallEntity={sipCall}
                    accounts={accounts}
                />
                <div className={classes.actionButtonSpacer}></div>
            </div>
        </div>)}
        {xdr_list?.map((xdr, indx) => <div key={'xdr_' + indx} className={classes.callEntry}>
            <div className={classes.callEntryLeftItem}>
                <div className={classes.callEntryIconContainer}>
                    {getCallIcon(xdr.bit_flags ?? 0, xdr.failed ?? 0)}
                </div>
                <div className={classes.callDetailsContainer}>
                    <div className={classes.caller}>{getCallerDestination(xdr)[0]}</div>
                    <div className={classes.secondLineContainer}>
                        <div className={classNames(classes.callerExt, xdr.failed && classes.failedText)}>
                            {getCallerDestination(xdr)[1]
                                ? t('screens:accountDashboard.ext') + ' ' + getCallerDestination(xdr)[1]
                                : ''
                            }
                        </div>
                        {getCallerDestination(xdr)[1] && (
                            <div className={classNames(classes.secondLineSeparator, xdr.failed && classes.failedText)}>
                                • 
                            </div>
                        )}
                        <div className={xdr.failed ? classes.failedText : classes.durationText}>
                            {xdr.failed 
                                ? ((xdr.bit_flags ?? 0) == 0 || (xdr.bit_flags ?? 0) == 4)
                                    ? t('screens:accountDashboard.theCallDidntConnect')
                                    : t('screens:accountDashboard.youMissedTheCall')
                                : formatDuration(xdr.charged_quantity ?? 0)
                            }
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.callEntryRightItem}>
                <div className={classes.callTimeContainer}>
                    <div className={classes.callTimeLabel}>
                        {formatDate(xdr.connect_time)[1]}
                    </div>
                    <div className={classes.callDateLabel}>
                        {formatDate(xdr.connect_time)[0]}
                    </div>
                </div>

                {xdr.cr_download_ids?.length ? (
                    <PlayFileButton
                        files={xdr.cr_download_ids?.map(e => { return {
                            i_xdr: xdr.i_xdr,
                            h323_conf_id: xdr.h323_conf_id,
                            call_recording_id: e,
                            playbackKey: cdrPlaybackKey(e, xdr.i_xdr)
                        } as PlayingFile; }) ?? []}
                        customClasses={{
                            beforePlaying: classes.playPauseButtonLoading
                        }}
                        dataQa={"play-file-icon"}
                        dataTestId={"play-file-icon-id"}
                        skipPermission
                    />
                ) : (
                    <div className={classes.noRecordingSpacer}></div>
                )}

                <CustomizedIconButton
                    tooltipText={t(
                        'tooltips:callSettings.viewCallDetailRecord',
                    )}
                    dataQa="details-icon-button"
                    dataTestId="details-icon-button"
                    onClick={() => {
                        setCallRecordInformation(xdr);
                    }}
                    skipPermission
                >
                    <Assignment/>
                </CustomizedIconButton>
            </div>
        </div>)}

        {!isLoadingCalls && totalCalls == 0 && !sipCallsList?.length && (
            <EmptyGridPlaceholder isVisible />
        )}

        {((totalCalls ?? 1) != (xdr_list?.length ?? 0)) && <AccountDashboardLoader loaderRef={loaderRef} />}
        
        <CallRecordInformationTabsDialog
            isOpen={isDetailsModalOpen}
            toggleVisibility={() => {
                setIsDetailsModalOpen(prev => !prev);
                setCallRecordInformation(undefined);
            }}
            callHistory={callRecordInformation}
            timezoneOffset={timezoneOffset}
            filesQueue={filesList}
            userDateTimeFormat={dateFormat}
            customerCurrency={currency}
            onDownloadClick={() =>
                callRecordInformation?.i_xdr &&
                    downloadFile(
                    callRecordInformation.i_xdr,
                    callRecordInformation.cr_download_ids?.[0] ||
                    '',
                    prepareCallRecordName(
                        callRecordInformation,
                        timezoneOffset,
                    ),
                    0,
                )
            }
            onDeleteClick={() => {
                setIsRemoveModalOpen(true);
            }}
            isDownloadingGoingOn={isDownloadingGoingOn}
            deletePermission={AccountPermission.Dashboard.CallActivity.value}
            downloadPermission={AccountPermission.Dashboard.CallActivity.value}
            downloadTranscriptionPermission={AccountPermission.Dashboard.CallActivity.value}
            playPermission={AccountPermission.Dashboard.CallActivity.value}
        />

        <AlertDialog
            isOpen={isRemoveModalOpen}
            dataQa="remove-call-history"
            contentClass="medium-width-modal-delete"
            hideHeader={true}
            description={
                <span style={{ whiteSpace: 'pre-wrap' }}>
                    {t('screens:calls.deleteCallHistory', {
                        name:
                            callRecordInformation &&
                            prepareCallRecordName(
                                callRecordInformation,
                                timezoneOffset,
                            ),
                    })}
                </span>
            }
            dialogActionsButtons={[
                <DialogButton
                    key="cancel"
                    label={t('common:cancel')}
                    onClick={() => setIsRemoveModalOpen(false)}
                />,
                <DialogButton
                    key="delete"
                    label={t('common:delete')}
                    className={classes.primaryModalButton}
                    onClick={deleteCallRecording}
                />,
            ]}
        />
            
        <WebSocketDataProvider
            callStatusUpdate={callStatusUpdateCallback}
            recalcCallsDurationEvent={recalcCallsDurationEvent}
            webSocketURL={Config.ACCOUNT_WEBSOCKET_SERVER_URL}
        >
        </WebSocketDataProvider>
            
        <AudioPlayer
            dataQa={'audio-player-call-queue'}
            dataTestId={'audio-player-call-queue'}
            visible={false}
            customClasses={{
                rootContainer: classes.audioPlayerRootContainer
            }}
        />
    </AccountDashboardWidgetBase>);
}

export default AccountDashboardCallActivityWidget;